import * as React from 'react'

import { MobXProviderContext, observer } from 'mobx-react'

import { TableFilters } from '~/client/src/desktop/components/TableFilters'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { useTableList } from '~/client/src/desktop/tables/useTableList'
import SearchInput from '~/client/src/shared/components/Filters/SearchInput/SearchInput'
import { Loader } from '~/client/src/shared/components/Loader'
import { ViewOptionsPopover } from '~/client/src/shared/components/ViewOptions'
import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import WhiteListItemsStore from '~/client/src/shared/stores/domain/WhiteListItems.store'

import { buildCompanyFields, COMPANY_INITIAL_QUERY } from './companyFields'
import {
  getCompanySelectionActions,
  getMergeCompanyIds,
} from './companySelectionActions'
import Header from './compound/Header'
import Modals from './compound/Modals'
import SelectionToolbar from './compound/SelectionToolbar'
import CompaniesTable from './compound/Table'
import { useCompanyMutations } from './hooks/useCompanyMutations'
import { useCompanyRecords } from './hooks/useCompanyRecords'
import { useModalState } from './hooks/useModalState'
import { CompanyDirectoryColumnId } from './types'

interface ProvidedStores {
  whiteListItemsStore: WhiteListItemsStore
  state: DesktopInitialState
}

function CompaniesDirectory(): React.ReactElement {
  const { whiteListItemsStore, state } = React.useContext(
    MobXProviderContext,
  ) as unknown as ProvidedStores

  const records = useCompanyRecords()
  const modalState = useModalState()
  const mutations = useCompanyMutations()

  React.useEffect(() => {
    void whiteListItemsStore.requestAllItems()
    return () => whiteListItemsStore.clear()
  }, [whiteListItemsStore])

  const tableFields = React.useMemo(() => buildCompanyFields(), [])
  const tableList = useTableList({
    records: records.rows,
    schema: tableFields.schema,
    displayColumns: tableFields.columns,
    columnConfig: {
      options: tableFields.columnConfig,
      storageKey: LocalStorageKey.CompanyDirectoryColumnConfig,
      projectId: state.activeProject?.id,
      userId: state.user?.id,
    },
    selectable: true,
    selectionColumnId: CompanyDirectoryColumnId.Checkbox,
    initialQuery: COMPANY_INITIAL_QUERY,
    groupingAllowNone: true,
  })

  const selectedRows = tableList.selection.selectedRecords
  const isSTXAdmin = !!state.isSTXAdmin
  const selectionActions = React.useMemo(
    () => getCompanySelectionActions(selectedRows, isSTXAdmin),
    [selectedRows, isSTXAdmin],
  )

  const { openMergeCompanies } = modalState
  const { nodes } = tableList.model
  const handleMerge = React.useCallback(() => {
    openMergeCompanies(getMergeCompanyIds(selectedRows, nodes))
  }, [openMergeCompanies, selectedRows, nodes])

  if (records.loading) {
    return (
      <section className="tw:flex tw:h-full tw:w-full tw:flex-col tw:items-center tw:justify-center">
        <Loader />
      </section>
    )
  }

  return (
    <section className="tw:flex tw:h-full tw:w-full tw:flex-col tw:py-4">
      <Header />

      <div className="tw:flex tw:items-center tw:gap-2 tw:border-0 tw:border-b tw:border-solid tw:border-border tw:px-4 tw:pb-2">
        <SearchInput {...tableList.searchProps} />
        <TableFilters {...tableList.filterProps} />
        <div className="tw:flex-1" />
        <ViewOptionsPopover {...tableList.viewOptionsProps} />
      </div>

      <SelectionToolbar
        list={tableList}
        merge={selectionActions.merge}
        onEdit={modalState.openBulkEdit}
        onMerge={handleMerge}
        onAddCompany={modalState.openAddCompany}
      />

      <CompaniesTable
        list={tableList}
        onEditCompany={modalState.openEditCompany}
      />

      <Modals
        modalState={modalState}
        mutations={mutations}
        selectedRows={selectedRows}
        statusAction={selectionActions.statusAction}
        clearSelection={tableList.selection.clear}
      />
    </section>
  )
}

export default observer(CompaniesDirectory)
